import { removeHostedBasePath, trimTrailingSlashes } from '@eneco/common/path';

/**
 * The path GA4 reports on. Query string and hash are dropped (they end up in
 * `page_location` instead), the hosted-PR prefix is removed so preview
 * deployments group with production, and the trailing slash is normalized away
 * so `/foo` and `/foo/` are one page.
 */
export const normalizePagePath = (href: string): string => {
  let pathname: string;

  try {
    pathname = new URL(href, 'https://placeholder.invalid').pathname;
  } catch {
    return '/';
  }

  const path = trimTrailingSlashes(removeHostedBasePath(pathname).replace(/\/{2,}/g, '/'));

  return path || '/';
};

/**
 * `page_location` for a path the caller supplied explicitly (a virtual page,
 * or a real navigation reported eagerly — see `usePageViewTracking`), rather
 * than read from `window.location.href`.
 *
 * The pathname is normalized the same way `normalizePagePath` normalizes it
 * (so a trailing slash cannot make `page_path` and `page_location` disagree),
 * but the query string and hash are preserved verbatim: a flow appends
 * `?sc_trk=...` for experiment attribution and sticky query params to the path
 * it is about to navigate to, and per `normalizePagePath`'s own contract, that
 * belongs in `page_location`, not in `page_path`.
 */
export const buildPageLocation = (explicitPath: string, origin: string): string => {
  let url: URL;

  try {
    url = new URL(explicitPath, origin);
  } catch {
    return `${origin}/`;
  }

  return `${origin}${normalizePagePath(explicitPath)}${url.search}${url.hash}`;
};

/**
 * The referrer as an absolute URL without query string or hash. Returns an
 * empty string when there is no referrer, or when it cannot be parsed.
 */
export const normalizeReferrer = (referrer: string): string => {
  if (!referrer) return '';

  try {
    const url = new URL(referrer);
    url.search = '';
    url.hash = '';
    return url.href;
  } catch {
    return '';
  }
};
